import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  model,
  OnInit,
  output,
  OutputEmitterRef,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PopoverModule } from 'primeng/popover';
import { DmButtonLibComponent } from '@everymatrix-base-components/dm-button-lib';
import {
  GlobalMessageService,
  ReportTemplate,
  TemplateService,
} from '@everymatrix-base-components/utils';
import { take, tap } from 'rxjs';
import { DeleteTemplateModalComponent } from '../delete-template-modal/delete-template-modal';
import { InputText } from 'primeng/inputtext';
import { NgClass, NgStyle } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { SendSettingsModalComponent } from '../send-settings-modal/send-settings-modal';
import { ProgressSpinner } from 'primeng/progressspinner';

@Component({
  selector: 'lib-template-select-modal',
  standalone: true,
  imports: [
    PopoverModule,
    FormsModule,
    DmButtonLibComponent,
    DeleteTemplateModalComponent,
    InputText,
    NgClass,
    TooltipModule,
    SendSettingsModalComponent,
    NgStyle,
    ProgressSpinner,
  ],
  templateUrl: './template-select-modal.html',
  styleUrls: ['./template-select-modal.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplateSelectModalComponent implements OnInit {
  selectedTemplateId!: string;
  searchQuery = '';
  runReportEmitter = output();
  csvLoadEmitter: OutputEmitterRef<ReportTemplate> = output();
  templateService = inject(TemplateService);
  messageService = inject(GlobalMessageService);
  selectedTemplate!: ReportTemplate;
  reportId = input.required<number>();
  showDeleteConfirmation = model(false);
  templateCsvLoadingConfig = model({ templateId: '', isLoading: false });
  showSendingModal = model(false);
  templateSignalList = this.templateService.templatesList;
  templateDeleteLoading = model(false);
  loading = signal(false);
  templateCsvLoadingIds = new Set<string>();
  templateDuplicateLoadingIds = new Set<string>();

  constructor() {
    effect(() => {
      this.selectedTemplateId =
        this.templateService.template()?.template_id || '';
    });
    effect(() => {
      this.templateCsvLoadingIds.delete(
        this.templateCsvLoadingConfig().templateId
      );
    });
  }

  ngOnInit() {
    this.loading.set(true);
    this.templateService
      .getAllTemplates(this.reportId())
      .pipe(
        take(1),
        tap((templates) => {
          this.templateService.templatesList.set(
            templates.filter((item) => !item.is_temporary)
          );
        })
      )
      .subscribe(() => this.loading.set(false));
  }

  deleteTemplate() {
    this.showDeleteConfirmation.set(true);
    this.templateService
      .deleteTemplate(this.selectedTemplateId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.templateDeleteLoading.set(false);
          this.showDeleteConfirmation.set(false);

          this.templateSignalList.update((arr) => {
            return (arr as ReportTemplate[]).filter(
              (t) => t.template_id !== this.selectedTemplateId
            );
          });

          if (
            !this.templateSignalList()?.length ||
            this.selectedTemplateId ===
              this.templateService.template()?.template_id
          ) {
            this.editTemplate(null);
          }

          this.messageService.showSuccessMessage({
            detail: 'Template is deleted successfully!',
          });
        },
        error: () => {
          this.templateDeleteLoading.set(false);
          this.messageService.showErrorMessage({
            detail: 'Failed to delete template.',
          });
        },
      });
  }

  duplicateTemplate(template: ReportTemplate, index: number) {
    this.templateDuplicateLoadingIds.add(template.template_id);
    this.templateService
      .saveTemplate(
        `${template.template_name}_duplicated`,
        template.template_desc,
        template.input_params,
        template.report_id,
        null,
        false
      )
      .pipe(take(1))
      .subscribe((duplicated) => {
        this.templateSignalList.update((arr) => {
          const list = [...(arr as ReportTemplate[])];
          list.splice(index + 1, 0, duplicated);
          return list;
        });
        this.templateDuplicateLoadingIds.delete(template.template_id);
        this.messageService.showSuccessMessage({
          detail: 'Template is duplicated successfully!',
        });
      });
  }

  editTemplate(template: ReportTemplate | null): void {
    if (template) {
      if (
        template.template_id !== this.templateService.template()?.template_id
      ) {
        this.templateService.setUrlTemplateId(template.template_id);
      }

      this.templateService.template.set(template);
      this.templateService.templateChangedSubject.next(template);
    } else {
      const url = new URL(window.location.href);
      url.searchParams.delete('templateId');
      window.history.pushState({}, '', url.toString());
      this.templateService.template.set(null);
      this.templateService.templateChangedSubject.next(null);
    }
  }

  runReport(template: ReportTemplate): void {
    if (this.templateService.template()?.template_id !== template.template_id) {
      this.templateService.setUrlTemplateId(template.template_id);
      this.templateService.templateChangedSubject.next(template);
      this.templateService.template.set(template);
    }
    this.runReportEmitter.emit();
  }

  runCsv(template: ReportTemplate): void {
    this.templateCsvLoadingIds.add(template.template_id);
    this.csvLoadEmitter.emit(template);
  }

  get filteredUnselectedFields(): ReportTemplate[] {
    if (!this.searchQuery.trim()) {
      return this.templateSignalList() as ReportTemplate[];
    }
    return (this.templateSignalList() as ReportTemplate[]).filter((item) =>
      item.template_name.toLowerCase().includes(this.searchQuery.toLowerCase())
    );
  }
}
